import L from 'leaflet'
import IconPlus from 'assets/images/png/IcPlus.png'
import './customMarkerIcon.css'
import 'leaflet/dist/leaflet.css'
import React, { useRef } from 'react'
import { Marker, MarkerProps, Popup } from 'react-leaflet'
import { usePoolingMapStore } from 'store/poolingMap'
import { DeliveriesType } from 'pages/PoolingMap/type'
import { ActionPostMessageEnum } from 'constants/postMessage'
import styled from '@emotion/styled'
import OrderInfoContent from './OrderInfoContent'

type CustomMarkerProps = MarkerProps & {
  delivery: DeliveriesType[number]
}
interface ColorMappingInterface {
  color: string
  bg: string
}

const PopupStyled = styled(Popup)`
  .leaflet-popup-tip {
    display: none;
  }
  .leaflet-popup-content p {
    margin: 0;
  }
  .leaflet-popup-content-wrapper {
    position: absolute;
    padding: 0 !important;
  }
`

const CustomMarker: React.FC<CustomMarkerProps> = ({ position, delivery }) => {
  const markerRef = useRef<L.Marker>(null)
  const { setSelectedOrder, selectedOrder, setRemoveOrder } =
    usePoolingMapStore()
  const orderSequenceIndex = selectedOrder.findIndex(
    (order) => order.order_id === delivery.order_id
  )
  const orderSequence = orderSequenceIndex === -1 ? '' : orderSequenceIndex + 1
  const SLAColors = (status: string) => {
    const label = status.toLowerCase()
    const tagColors: { [key: string]: ColorMappingInterface } = {
      redTag: { color: '#F94D63', bg: '#FEEDEF' },
      blueTag: { color: '#246EE5', bg: '#E9F1FC' },
      greenTag: { color: '#00AA5B', bg: '#E6F7EF' },
      purpleTag: { color: '#6847BB', bg: '#F0EDF8' },
    }
    const colorMappings: { [key: string]: ColorMappingInterface } = {
      '15 menit': tagColors.blueTag,
      '30 menit': tagColors.blueTag,
      '45 menit': tagColors.purpleTag,
      '1 jam': tagColors.purpleTag,
      '2 jam': tagColors.blueTag,
      '4 jam': tagColors.purpleTag,
      '2 - 4 jam': tagColors.purpleTag,
      'same day': tagColors.greenTag,
    }
    if (label.toLowerCase().includes('sameday')) {
      return colorMappings['same day']
    }
    return (
      colorMappings[label?.toLowerCase()] || {
        color: '#6D7588',
        bg: '#F3F6FA',
      }
    )
  }

  const timingDescriptionParser = (timing: string) => {
    if (timing.toLowerCase().includes('sameday')) {
      return timing.split(' (')[0]
    }
    if (timing.toLowerCase().includes('jadwal pengiriman')) {
      return 'Scheduled instant'
    }
    return timing
  }

  const customMarkerIcon = new L.DivIcon({
    html: `
    <div class='marker-content'>
      <svg style='width:40' viewBox="0 0 49 48" fill="none" xmlns="http://www.w3.org/2000/svg" class="custom-marker-icon">
        <mask id="path-1-inside-1_306_112398" fill="white">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M33.3025 35.4038C38.2165 32.4372 41.5 27.0619 41.5 20.9231C41.5 11.5767 33.8888 4 24.5 4C15.1112 4 7.5 11.5767 7.5 20.9231C7.5 27.0619 10.7835 32.4373 15.6976 35.4038C16.2472 35.777 16.8119 36.1334 17.3908 36.4722C18.9473 37.3605 20.3202 38.4717 21.4516 39.7586C22.3924 40.827 23.141 42.281 23.6594 43.492C23.7234 43.6412 23.8383 43.7698 23.9885 43.8606C24.1387 43.9514 24.3172 44 24.5 44C24.6828 44 24.8613 43.9514 25.0115 43.8606C25.1618 43.7698 25.2766 43.6412 25.3406 43.492C25.859 42.281 26.6076 40.827 27.5484 39.7586C28.6798 38.4717 30.0527 37.3605 31.6092 36.4722C32.1882 36.1334 32.7529 35.777 33.3025 35.4038Z"/>
        </mask>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M33.3025 35.4038C38.2165 32.4372 41.5 27.0619 41.5 20.9231C41.5 11.5767 33.8888 4 24.5 4C15.1112 4 7.5 11.5767 7.5 20.9231C7.5 27.0619 10.7835 32.4373 15.6976 35.4038C16.2472 35.777 16.8119 36.1334 17.3908 36.4722C18.9473 37.3605 20.3202 38.4717 21.4516 39.7586C22.3924 40.827 23.141 42.281 23.6594 43.492C23.7234 43.6412 23.8383 43.7698 23.9885 43.8606C24.1387 43.9514 24.3172 44 24.5 44C24.6828 44 24.8613 43.9514 25.0115 43.8606C25.1618 43.7698 25.2766 43.6412 25.3406 43.492C25.859 42.281 26.6076 40.827 27.5484 39.7586C28.6798 38.4717 30.0527 37.3605 31.6092 36.4722C32.1882 36.1334 32.7529 35.777 33.3025 35.4038Z" fill='${
          delivery?.color || 'grey'
        }'/>
        <path d="M33.3025 35.4038L32.7857 34.5477L32.7628 34.5615L32.7407 34.5765L33.3025 35.4038ZM15.6976 35.4038L16.2594 34.5766L16.2373 34.5615L16.2144 34.5477L15.6976 35.4038ZM17.3908 36.4722L16.8856 37.3353L16.8951 37.3407L17.3908 36.4722ZM21.4516 39.7586L20.7006 40.4189L20.7011 40.4195L21.4516 39.7586ZM23.6594 43.492L22.7401 43.8856L22.7405 43.8865L23.6594 43.492ZM23.9885 43.8606L24.5057 43.0047L24.5057 43.0047L23.9885 43.8606ZM25.0115 43.8606L24.4943 43.0047L24.4943 43.0047L25.0115 43.8606ZM25.3406 43.492L26.2595 43.8865L26.2599 43.8856L25.3406 43.492ZM27.5484 39.7586L28.2989 40.4195L28.2994 40.4189L27.5484 39.7586ZM31.6092 36.4722L32.105 37.3407L32.1143 37.3352L31.6092 36.4722ZM33.8193 36.2599C39.0195 33.1206 42.5 27.428 42.5 20.9231H40.5C40.5 26.6958 37.4136 31.7539 32.7857 34.5477L33.8193 36.2599ZM42.5 20.9231C42.5 11.0202 34.4368 3 24.5 3V5C33.3408 5 40.5 12.1333 40.5 20.9231H42.5ZM24.5 3C14.5632 3 6.5 11.0202 6.5 20.9231H8.5C8.5 12.1333 15.6592 5 24.5 5V3ZM6.5 20.9231C6.5 27.428 9.98058 33.1207 15.1808 36.2599L16.2144 34.5477C11.5864 31.7539 8.5 26.6958 8.5 20.9231H6.5ZM15.1358 36.2311C15.704 36.617 16.2877 36.9853 16.8857 37.3352L17.8959 35.6091C17.3361 35.2815 16.7903 34.9371 16.2594 34.5766L15.1358 36.2311ZM16.8951 37.3407C18.36 38.1767 19.6455 39.2187 20.7006 40.4189L22.2027 39.0984C20.9949 37.7246 19.5346 36.5443 17.8864 35.6037L16.8951 37.3407ZM20.7011 40.4195C21.5372 41.3689 22.236 42.708 22.7401 43.8856L24.5787 43.0985C24.046 41.854 23.2476 40.2851 22.2021 39.0978L20.7011 40.4195ZM22.7405 43.8865C22.8937 44.2435 23.1575 44.5269 23.4713 44.7164L24.5057 43.0047C24.519 43.0127 24.5532 43.039 24.5783 43.0976L22.7405 43.8865ZM23.4713 44.7164C23.7842 44.9055 24.1428 45 24.5 45V43C24.4986 43 24.4974 42.9999 24.4964 42.9998C24.4954 42.9996 24.4949 42.9995 24.495 42.9995C24.4951 42.9995 24.496 42.9998 24.4977 43.0005C24.4994 43.0012 24.5021 43.0025 24.5057 43.0047L23.4713 44.7164ZM24.5 45C24.8572 45 25.2158 44.9055 25.5287 44.7164L24.4943 43.0047C24.4979 43.0025 24.5006 43.0012 24.5023 43.0005C24.504 42.9998 24.5049 42.9995 24.505 42.9995C24.5051 42.9995 24.5046 42.9996 24.5036 42.9998C24.5026 42.9999 24.5014 43 24.5 43V45ZM25.5287 44.7164C25.8425 44.5269 26.1063 44.2435 26.2595 43.8865L24.4217 43.0976C24.4469 43.039 24.481 43.0127 24.4943 43.0047L25.5287 44.7164ZM26.2599 43.8856C26.764 42.708 27.4628 41.3689 28.2989 40.4195L26.7979 39.0978C25.7524 40.2851 24.9541 41.854 24.4213 43.0985L26.2599 43.8856ZM28.2994 40.4189C29.3545 39.2187 30.64 38.1767 32.1049 37.3407L31.1136 35.6037C29.4655 36.5443 28.0051 37.7246 26.7973 39.0984L28.2994 40.4189ZM32.1143 37.3352C32.7124 36.9852 33.2961 36.6169 33.8643 36.231L32.7407 34.5765C32.2098 34.9371 31.6639 35.2815 31.1041 35.6091L32.1143 37.3352Z" fill="#646464" mask="url(#path-1-inside-1_306_112398)"/>
        ${
          orderSequence !== ''
            ? `<text
              x="21"
              y="24.5"
              textAnchor="middle"
              dominantBaseline="middle"
              stroke="white" // Added stroke for better contrast
              strokeWidth="0.5"
            >
              ${orderSequence}
            </text>`
            : `<path
          d="M24.5 27C27.8137 27 30.5 24.3137 30.5 21C30.5 17.6863 27.8137 15 24.5 15C21.1863 15 18.5 17.6863 18.5 21C18.5 24.3137 21.1863 27 24.5 27Z"
          fill="white"
        />`
        }
        
        
      </svg>
      <div class="marker-info">
        ${
          orderSequence === ''
            ? `<div class='absolute-icon' id="${delivery?.order_id}-absolute-icon">
          <img src="${IconPlus}"/>
        </div>`
            : `<div class='absolute-icon-close' id="${delivery?.order_id}-absolute-icon">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M9.34532 0.633665C8.94438 0.23344 8.29433 0.23344 7.89339 0.633664L4.98119 3.54066L2.10601 0.670623C1.70507 0.270399 1.05502 0.270399 0.654078 0.670623C0.253138 1.07085 0.253137 1.71974 0.654077 2.11996L3.52925 4.99L0.633714 7.88036C0.232773 8.28059 0.232772 8.92948 0.633713 9.3297C1.03465 9.72993 1.68471 9.72993 2.08565 9.3297L4.98119 6.43934L7.91375 9.36666C8.31469 9.76689 8.96475 9.76689 9.36569 9.36666C9.76663 8.96644 9.76663 8.31755 9.36569 7.91732L6.43312 4.99L9.34532 2.083C9.74626 1.68278 9.74626 1.03389 9.34532 0.633665Z" fill="#646464"/>
</svg>

        </div>`
        }
        
        <span class='marker-number'>${delivery?.order_id}</span>
        <span class='marker-name'>${delivery?.destination.receiver.name}</span>
      </div>
      <div class="marker-button" style="background-color: ${
        SLAColors(delivery?.delivery_option?.timing_desc).bg
      }; color: ${
      SLAColors(delivery?.delivery_option?.timing_desc).color
    }; border: 1px solid ${
      SLAColors(delivery?.delivery_option?.timing_desc).color
    }; border-radius: 99px"}>
        <button class="button-sla">
          ${
            [15, 16, 17, 18].includes(delivery?.delivery_option?.timing_id)
              ? `<svg width="11" height="11" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M26.9344 9.05322L18.9344 3.57322C18.0708 2.98101 17.0483 2.66406 16.0011 2.66406C14.954 2.66406 13.9313 2.98101 13.0678 3.57322L5.06775 9.05322C4.32237 9.57496 3.71463 10.2697 3.29663 11.0778C2.87863 11.8859 2.66283 12.7834 2.66775 13.6932V23.7865C2.65361 24.5009 2.78035 25.2112 3.04074 25.8767C3.30114 26.542 3.69009 27.1497 4.18535 27.6648C4.68063 28.1799 5.27253 28.5923 5.92725 28.8785C6.58195 29.1648 7.28666 29.3193 8.00109 29.3332H16.6717H16.6796H24.4343C24.634 29.3332 24.8319 29.2939 25.0169 29.2173C25.3793 29.1411 25.7339 29.0277 26.0749 28.8785C26.7296 28.5923 27.3216 28.1799 27.8168 27.6648C28.3121 27.1497 28.7011 26.542 28.9615 25.8767C29.2219 25.2112 29.3485 24.5009 29.3344 23.7865V13.6932C29.3393 12.7834 29.1235 11.8859 28.7055 11.0778C28.2875 10.2697 27.6797 9.57496 26.9344 9.05322ZM15.5539 15.9201L17.4679 15.1507V15.1511C20.3968 13.9733 23.1948 16.8093 22.9879 20.024C22.8946 21.4799 22.0284 22.7021 20.7067 23.2445L11.3664 27V22.3284L16.8349 20.117C17.3528 19.9005 17.49 19.2453 17.1189 18.7618C16.8504 18.4123 16.3941 18.2645 16.0114 18.4032L14.7726 18.8517C10.6905 20.1964 8.38948 16.9534 9.13943 13.8049C9.40168 12.7043 10.5378 11.5602 11.9355 11.0754L19.2967 8V12.7484L14.8675 14.5228C14.41 14.7059 14.3128 15.2989 14.6753 15.6938C14.907 15.946 15.2618 16.0376 15.5539 15.9201Z" fill="${
                  SLAColors(delivery?.delivery_option?.timing_desc).color
                }"/>
</svg>`
              : `<svg width="9" height="11" viewBox="0 0 9 11" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.66834 3.61009H5.35275L5.50868 0.912981C5.51494 0.753719 5.47388 0.596369 5.39134 0.463506C5.3088 0.330606 5.18899 0.228981 5.04908 0.173069C4.90913 0.117156 4.7562 0.109806 4.61213 0.152144C4.46809 0.194519 4.34026 0.284369 4.24688 0.408831L0.527704 5.41988C0.445054 5.53144 0.393567 5.66562 0.379167 5.80699C0.36473 5.94833 0.387979 6.09121 0.446217 6.21912C0.504454 6.34703 0.595317 6.45481 0.708342 6.53014C0.821405 6.60548 0.952054 6.64527 1.08533 6.64497H3.40099L3.24503 9.33702C3.23877 9.49628 3.27983 9.65363 3.36237 9.78649C3.4449 9.91939 3.56472 10.0211 3.70467 10.077C3.84458 10.1329 3.9975 10.1402 4.14158 10.0979C4.28566 10.0555 4.41346 9.96563 4.50679 9.84117L8.226 4.83012C8.30723 4.71848 8.35755 4.58479 8.3712 4.44424C8.38489 4.30369 8.36134 4.16191 8.30329 4.03489C8.24524 3.90788 8.15498 3.80074 8.04274 3.72563C7.93054 3.65056 7.80083 3.61051 7.66834 3.61009Z" fill=${
              SLAColors(delivery?.delivery_option?.timing_desc).color
            } />
          </svg>`
          }
          ${timingDescriptionParser(delivery?.delivery_option?.timing_desc)}
         </button>
      </div>
    </div>
    `,
    className: 'custom-marker-with-html',
    iconSize: [48, 48],
    iconAnchor: [24, 48],
    popupAnchor: [58, 0],
  })
  const handleHover = () => {
    markerRef.current?.openPopup()
  }

  // Close popup on mouseout
  const handleMouseOut = () => {
    markerRef.current?.closePopup()
  }

  const handleClick = () => {
    setSelectedOrder(delivery)

    if (window.parent) {
      window.parent.postMessage(
        {
          action: ActionPostMessageEnum.SELECT_ORDER,
          payload: delivery,
        },
        '*'
      )
    }
  }

  const handleRemove = () => {
    setRemoveOrder(delivery)
    if (window.parent) {
      window.parent.postMessage(
        {
          action: ActionPostMessageEnum.REMOVE_ORDER,
          payload: delivery,
        },
        '*'
      )
    }
  }

  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={customMarkerIcon}
      eventHandlers={{
        mouseover: handleHover,
        mouseout: handleMouseOut,
        click: orderSequence === '' ? handleClick : handleRemove,
      }}
      zIndexOffset={1000}
    >
      <PopupStyled>
        <OrderInfoContent delivery={delivery} />
      </PopupStyled>
    </Marker>
  )
}

export default CustomMarker
